import { Link, useLocation } from 'react-router';
import { usePlaylist, usePlaylistMutations, usePlaylists, useSession } from '../../lib/queries';
import { EmptyState, Select, Skeleton } from '../../ui/kit';
import { AddTrackForm, TrackRow } from '../library/tracks';
import { useRoomStore } from '../../room/store';
import { LibraryLink } from './Panels';

/** Compact playlist manager inside the room. Full editing lives on /library. */
export function PlaylistPanel() {
  const session = useSession().data!;
  const loc = useLocation();
  const playlists = usePlaylists(!!session.user);
  const activeId = session.user?.activePlaylistId ?? null;
  const active = usePlaylist(activeId);
  const m = usePlaylistMutations();
  // The room's per-user cursor decides what plays next here (it can differ between rooms).
  const nextItemId = useRoomStore((s) => s.data?.me.nextTrack?.itemId ?? null);

  if (!session.user) {
    return (
      <div className="p-4">
        <EmptyState
          title="Playlists need an account"
          body="Sign in to build a playlist, then join the DJ queue."
          action={
            <Link
              className="text-sm font-medium text-accent"
              to={`/signin?returnTo=${encodeURIComponent(loc.pathname)}`}
            >
              Sign in
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="cs-scroll flex h-full flex-col gap-4 overflow-y-auto p-4">
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <label
            htmlFor="active-pl"
            className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-fg-subtle"
          >
            Active playlist (used for your DJ turns)
          </label>
          {playlists.isPending ? (
            <Skeleton className="h-10" />
          ) : (
            <Select
              id="active-pl"
              value={activeId ?? ''}
              onChange={(v) => m.setActive.mutate(v || null)}
              options={[
                { value: '', label: 'None selected' },
                ...(playlists.data ?? []).map((p) => ({
                  value: p.id,
                  label: `${p.name} (${p.itemCount})`,
                })),
              ]}
            />
          )}
        </div>
      </div>
      <LibraryLink />
      {!activeId ? (
        <EmptyState
          title={
            (playlists.data?.length ?? 0) === 0 ? 'No playlists yet' : 'Choose an active playlist'
          }
          body={
            (playlists.data?.length ?? 0) === 0
              ? 'Create one in your library, add a few YouTube links, and come back.'
              : 'Your DJ turns play from the active playlist, in order, looping.'
          }
          action={
            (playlists.data?.length ?? 0) === 0 ? (
              <Link to="/library" className="text-sm font-medium text-accent">
                Create a playlist
              </Link>
            ) : undefined
          }
        />
      ) : (
        <>
          <AddTrackForm playlistId={activeId} compact />
          {active.isPending ? (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : active.data && active.data.items.length === 0 ? (
            <p className="text-sm text-fg-muted">
              This playlist is empty. Paste a YouTube link above.
            </p>
          ) : (
            <ol className="flex flex-col gap-1">
              {active.data?.items.map((it, i) => (
                <TrackRow
                  key={it.id}
                  item={it}
                  index={i}
                  total={active.data!.items.length}
                  playlistId={activeId}
                  isNext={it.id === nextItemId}
                  compact
                />
              ))}
            </ol>
          )}
        </>
      )}
    </div>
  );
}
